import { useState, useEffect, useRef } from 'react';
import { getThreadMessages, postThreadMessage } from '../services/supportThreadApi';
import { toast } from 'react-toastify';

/**
 * Custom hook to manage thread state and operations
 * @param {string} reportId - The ID of the support report
 * @returns {Object} Thread state and operations
 */
const useThread = reportId => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch thread messages when reportId changes
  useEffect(() => {
    if (reportId) {
      fetchMessages();
    }
  }, [reportId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Function to scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch thread messages
  const fetchMessages = async () => {
    if (!reportId) {
      console.warn('[THREAD] No reportId provided, skipping fetch');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null); // Clear previous errors

    try {
      console.log(`[THREAD] Fetching messages for report: ${reportId}`);
      const data = await getThreadMessages(reportId);

      if (Array.isArray(data)) {
        console.log(`[THREAD] Successfully received ${data.length} messages`);
        setMessages(data);
      } else {
        console.warn('[THREAD] Received non-array data:', data);
        // Handle case where API returns unexpected format
        setMessages([]);
        // Don't show error to user as the API might return empty object for no messages
      }
    } catch (err) {
      console.error(`[THREAD] Error fetching messages for report ${reportId}:`, err);
      setError('Failed to load thread messages');
      // Show a more user-friendly error message
      toast.error('Unable to load messages. Please try again later.');
      // Set empty array to prevent UI from breaking
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Submit new message
  const submitReply = async () => {
    if (!replyText.trim()) {
      toast.warn('Please enter a message');
      return;
    }

    if (!reportId) {
      console.error('[THREAD] Cannot submit reply: No reportId provided');
      toast.error('Cannot send message: Report ID is missing');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare message data for the backend
      // The backend expects {message: "text"}
      const messageData = {
        message: replyText.trim(),
      };

      console.log(`[THREAD] Posting message to report: ${reportId}`, messageData);

      // Optimistically add the message to the UI first
      const tempMessage = {
        id: `temp-${Date.now()}`,
        message: replyText.trim(),
        createdAt: new Date().toISOString(),
        isOptimistic: true, // Flag to identify optimistic updates
      };

      setMessages(prevMessages => [...prevMessages, tempMessage]);

      // Actually send the message to the server
      await postThreadMessage(reportId, messageData);
      console.log('[THREAD] Message posted successfully');

      // Clear the form
      setReplyText('');
      setIsReplyOpen(false);
      toast.success('Reply sent successfully');

      // Wait a brief moment for the backend to process the message
      setTimeout(async () => {
        // Refresh messages to ensure we have the latest from server
        // This will replace our optimistic message with the real one
        await fetchMessages();
      }, 500);
    } catch (err) {
      console.error(`[THREAD] Error posting message to report ${reportId}:`, err);
      toast.error('Failed to send reply. Please try again.');

      // Remove the optimistic message if the request failed
      setMessages(prevMessages => prevMessages.filter(msg => !msg.isOptimistic));
    } finally {
      setSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = timestamp => {
    if (!timestamp) return 'Just now';

    // If timestamp is a Firestore timestamp, convert it
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

    // Format as relative time if recent, otherwise show date
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    // For older messages, show the date
    return date.toLocaleString();
  };

  return {
    messages,
    loading,
    error,
    isReplyOpen,
    replyText,
    submitting,
    messagesEndRef,
    setIsReplyOpen,
    setReplyText,
    fetchMessages,
    submitReply,
    formatDate,
  };
};

export default useThread;
